import { DTOAdapter } from '@services/DTOAdapter'
import { ErrorResponse } from '@app/HTTPErrorOverwriteInterceptor'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { EMPTY, Observable, catchError, map, of, tap, throwError } from 'rxjs'
import { AuthHandoffService } from './auth-handoff.service'
import { AuthenticatedCacheService } from '@services/authenticated-cache.service'
import { Organisation } from '@app/domain/Organisation'
import { OrganisationAuditEntry } from '@app/domain/OrganisationAuditEntry'
import { OrganisationAuditEntryDTO } from '@services/DTO/OrganisationAuditEntryDTO'
import { OrganisationDTO } from '@services/DTO/OrganisationDTO'
import { Page } from '@app/domain/Page'
import { PageDTO } from '@services/DTO/PageDTO'
import { PatchRequestBody } from '@services/DTO/PatchRequestBody'
import { TokenRepositoryService } from './token-repository.service'
import { WindowProvider } from '@app/helpers/providers/window.provider'
import { environment } from '@environments/environment'

@Injectable({
    providedIn: 'root',
})
export class OrganisationService {

    private static readonly ORGANISATION_CACHE_KEY = 'organisation.current'

    constructor(
        private http: HttpClient,
        private dtoAdapter: DTOAdapter,
        private authHandoffService: AuthHandoffService,
        private tokenRepositoryService: TokenRepositoryService,
        private windowProvider: WindowProvider,
        private authenticatedCacheService: AuthenticatedCacheService
    ) { }

    get(): Observable<Organisation> {
        return this.getOrganisationDto()
            .pipe(
                catchError(error => this.handleGetError(error)),
                map(dto => this.dtoAdapter.adaptOrganisationDto(dto))
            )
    }

    getCached(): Observable<Organisation> {
        return this.getOrganisationDto()
            .pipe(
                tap(dto => this.authenticatedCacheService.store(
                    OrganisationService.ORGANISATION_CACHE_KEY,
                    dto
                )),
                catchError((error: unknown) => {
                    if (this.isNotFoundError(error)) {
                        this.redirectToSignup()
                        return EMPTY
                    }
                    if (this.isAuthenticationError(error)) {
                        return throwError(() => error)
                    }
                    const cachedDto = this.authenticatedCacheService.retrieve<OrganisationDTO>(
                        OrganisationService.ORGANISATION_CACHE_KEY
                    )
                    if (cachedDto !== null) {
                        return of(cachedDto)
                    }
                    return throwError(() => error)
                }),
                map(dto => this.dtoAdapter.adaptOrganisationDto(dto))
            )
    }

    patch(body: PatchRequestBody): Observable<Organisation> {
        const url = `${environment.apiBaseURL}/organisation`

        return this.http.patch<OrganisationDTO>(
            url,
            body
        )
            .pipe(
                map(dto => this.dtoAdapter.adaptOrganisationDto(dto))
            )
    }

    listAuditLog(
        organisationId: string,
        page: number,
        pageSize: number,
    ): Observable<Page<OrganisationAuditEntry>> {
        const path = `/organisation/${organisationId}/audit-log/list`
        const url = new URL(path, environment.apiBaseURL)
        const params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString())

        return this.http.get<PageDTO<OrganisationAuditEntryDTO>>(url.toString(), { params })
            .pipe(
                map(pageDto => ({
                    items: pageDto.items.map(dto => this.dtoAdapter.adaptOrganisationAuditEntryDto(dto)),
                    page: pageDto.page,
                    pageSize: pageDto.pageSize,
                    totalItems: pageDto.totalItems,
                }))
            )
    }

    private getOrganisationDto(): Observable<OrganisationDTO> {
        const url = new URL('/organisation', environment.apiBaseURL)
        return this.http.get<OrganisationDTO>(url.toString())
    }

    private handleGetError(error: unknown): Observable<OrganisationDTO> {
        if (this.isNotFoundError(error)) {
            this.redirectToSignup()
            return EMPTY
        }
        return throwError(() => error)
    }

    private isNotFoundError(error: unknown): boolean {
        return typeof error === 'object'
            && error !== null
            && (error as Partial<ErrorResponse>).status === 404
    }

    private isAuthenticationError(error: unknown): boolean {
        return typeof error === 'object'
            && error !== null
            && (
                (error as Partial<ErrorResponse>).status === 401
                || (error as Partial<ErrorResponse>).status === 403
            )
    }

    private redirectToSignup() {
        console.warn('Organisation not found, redirecting to signup')
        const accessToken = this.tokenRepositoryService.getAccessToken()
        if (accessToken === null) {
            console.error('No access token found, cannot redirect to signup')
            return
        }
        const refreshToken = this.tokenRepositoryService.getRefreshToken()
        this.authHandoffService.save({ accessToken, refreshToken })
        this.windowProvider.navigateToUrl(environment.signupBaseURL)
    }
}
